import React, { useEffect, useState } from 'react';
import { apiGet } from '../../services/apiServices';
import { endPoints } from '../../configs/apiUrls';
import { toast } from 'react-toastify';
import { FaUsers, FaUserTie, FaFileAlt, FaCheckCircle, FaClock } from 'react-icons/fa';

const Dashboard = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res: any = await apiGet(endPoints.DASHBOARD_STATS);
      if (res.success) {
        setStats(res.data);
      } else {
        toast.error("Failed to load dashboard data");
      }
    } catch (error) {
      toast.error("Error fetching dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading Dashboard...</div>;
  }

  return (
    <div className="max-w-9xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Admin Dashboard</h1>

      {/* --- STATS CARDS GRID --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        
        {/* Users Card */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="p-4 bg-blue-50 text-blue-600 rounded-lg mr-4">
            <FaUsers className="text-2xl" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Customers</p>
            <p className="text-2xl font-bold text-gray-800">{stats?.users?.customers || 0}</p>
          </div>
        </div>

        {/* Valuers Card */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="p-4 bg-purple-50 text-purple-600 rounded-lg mr-4">
            <FaUserTie className="text-2xl" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Agents</p>
            <p className="text-2xl font-bold text-gray-800">{stats?.users?.agents || 0}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="p-4 bg-purple-50 text-purple-600 rounded-lg mr-4">
            <FaUserTie className="text-2xl" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Valuers</p>
            <p className="text-2xl font-bold text-gray-800">{stats?.users?.valuers || 0}</p>
          </div>
        </div>

        {/* Total Requests Card */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="p-4 bg-indigo-50 text-indigo-600 rounded-lg mr-4">
            <FaFileAlt className="text-2xl" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Requests</p>
            <p className="text-2xl font-bold text-gray-800">{stats?.requests?.total || 0}</p>
          </div>
        </div>

        {/* Pending Requests Card */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="p-4 bg-orange-50 text-orange-600 rounded-lg mr-4">
            <FaClock className="text-2xl" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Pending Requests</p>
            <p className="text-2xl font-bold text-gray-800">{stats?.requests?.pending || 0}</p>
          </div>
        </div>
      </div>

      {/* --- RECENT REQUESTS TABLE --- */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800">Recent Requests</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-sm border-b">
                <th className="px-6 py-3 font-medium">Customer</th>
                <th className="px-6 py-3 font-medium">Type</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Assigned To</th>
                <th className="px-6 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-100">
              {stats?.recentActivity?.map((req: any) => (
                <tr key={req._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-800">
                    {req.createdBy?.name || "Unknown"}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{req.type}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium 
                      ${req.status === 'Requested' ? 'bg-orange-100 text-orange-700' : 
                        req.status === 'Completed' ? 'bg-green-100 text-green-700' : 
                        'bg-blue-100 text-blue-700'}`}>
                      {req.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {req.assignedTo?.name || <span className="text-gray-400 italic">Unassigned</span>}
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    {new Date(req.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              
              {stats?.recentActivity?.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No recent requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
    </div>
  );
};

export default Dashboard;